import { makeCanvas, ctxOf, drawRaster } from '@/engine/canvas';
import { TILE } from '@/gfx/tiles';
import { FLOOR_NAMES, type FloorId } from '@/world/types';
import { roomAtPx, type Floor } from '@/world/builder';
import type { Character } from './entities';
import type { Game } from './game';
import { drawFurnished } from './render';
import { text, panel, dimScreen } from './hud';
import { locationLabel } from './text';

const FLOORS: FloorId[] = ['basement', 'ground', 'second', 'third', 'attic'];
const AREA = { x: 52, y: 128, w: 624, h: 318 };
type Bounds = { x: number; y: number; w: number; h: number };
type FloorRecord = { seen: Uint8Array; bounds: Bounds | null; image?: HTMLCanvasElement };
type MapSession = { blueprint: boolean; floors: Map<FloorId, FloorRecord> };
const sessions = new WeakMap<Game, MapSession>();

/** Crop remembered maps around explored tiles and known friends, with room to orient. */
export function mapBounds(seen: Uint8Array, w: number, h: number, friends: Array<{ x: number; y: number }> = []): Bounds | null {
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  const include = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  };
  for (let i = 0; i < seen.length; i++) if (seen[i]) include(i % w, Math.floor(i / w));
  for (const c of friends) include(Math.floor(c.x / TILE), Math.floor(c.y / TILE));
  if (x1 < 0) return null;
  x0 = Math.max(0, x0 - 4); y0 = Math.max(0, y0 - 4);
  x1 = Math.min(w - 1, x1 + 4); y1 = Math.min(h - 1, y1 + 4);
  const width = Math.min(w, Math.max(20, x1 - x0 + 1)), height = Math.min(h, Math.max(16, y1 - y0 + 1));
  return { x: Math.min(w - width, Math.max(0, Math.floor((x0 + x1 + 1 - width) / 2))),
    y: Math.min(h - height, Math.max(0, Math.floor((y0 + y1 + 1 - height) / 2))), w: width, h: height };
}

export function mapLayout(bounds: Bounds) {
  const scale = Math.min(12, Math.floor(AREA.w / bounds.w), Math.floor(AREA.h / bounds.h));
  const w = bounds.w * scale, h = bounds.h * scale;
  return { scale, w, h, x: Math.round(AREA.x + (AREA.w - w) / 2), y: Math.round(AREA.y + (AREA.h - h) / 2) };
}

export function blueprintBounds(f: Pick<Floor, 'w' | 'h' | 'rooms'>): Bounds {
  const corners = f.rooms.flatMap(r => [{ x: r.x * TILE, y: r.y * TILE }, { x: (r.x + r.w - 1) * TILE, y: (r.y + r.h - 1) * TILE }]);
  return mapBounds(new Uint8Array(f.w * f.h), f.w, f.h, corners) ?? { x: 0, y: 0, w: f.w, h: f.h };
}

function friendsOn(g: Game, floor: FloorId): Character[] {
  return [...g.chars.values()].filter(c => c.floor === floor && c.state !== 'outside' && c.state !== 'escaped');
}

export function openMap(g: Game): void {
  const blueprint = !!g.flags.mapKnown;
  const floors = new Map<FloorId, FloorRecord>();
  for (const id of FLOORS) {
    const f = g.mansion.floors.get(id);
    if (!f) continue;
    const seen = new Uint8Array(f.w * f.h);
    if (f.memory) {
      const sample = makeCanvas(f.w, f.h), ctx = ctxOf(sample);
      ctx.imageSmoothingEnabled = true;
      drawRaster(ctx, f.memory, 0, 0, f.w, f.h);
      const pixels = ctx.getImageData(0, 0, f.w, f.h).data;
      for (let i = 0; i < seen.length; i++) seen[i] = pixels[i * 4 + 3] > 6 ? 1 : 0;
    }
    const explored = seen.some(Boolean);
    // A captive's unknown floor does not become navigable just because they exist.
    if (!blueprint && !explored && id !== g.leader.floor) continue;
    floors.set(id, { seen, bounds: blueprint ? blueprintBounds(f) : mapBounds(seen, f.w, f.h, friendsOn(g, id)) });
  }
  sessions.set(g, { blueprint, floors });
  g.mapFloor = g.leader.floor;
  g.overlay = 'map';
}

export function changeMapFloor(g: Game, delta: number): void {
  const session = sessions.get(g);
  if (!session) return;
  const available = FLOORS.filter(id => session.floors.has(id));
  if (!available.length) return;
  g.mapFloor = available[(available.indexOf(g.mapFloor) + Math.sign(delta) + available.length) % available.length];
  g.audio.ui();
}

export function clickMapFloor(g: Game): void {
  if (!g.input.pointerKnown) return;
  const x = g.input.pointerX * 2, y = g.input.pointerY * 2;
  if (y < 82 || y > 110) return;
  const index = Math.floor((x - 56) / 112), id = FLOORS[index];
  if (x < 56 || x - (56 + index * 112) > 104 || !sessions.get(g)?.floors.has(id)) return;
  g.mapFloor = id; g.audio.ui();
}

function isSeen(record: FloorRecord, f: Floor, x: number, y: number): boolean {
  return !!record.seen[Math.floor(y) * f.w + Math.floor(x)];
}

function rememberedImage(f: Floor, record: FloorRecord): HTMLCanvasElement {
  if (record.image) return record.image;
  const bounds = record.bounds!, layout = mapLayout(bounds), image = makeCanvas(layout.w, layout.h), ctx = ctxOf(image);
  if (record.seen.some(Boolean) && f.memory) {
    const crop = [bounds.x * TILE, bounds.y * TILE, bounds.w * TILE, bounds.h * TILE, 0, 0, layout.w, layout.h] as const;
    ctx.imageSmoothingEnabled = true;
    drawFurnished(ctx, f, { x: crop[0], y: crop[1], w: crop[2], h: crop[3] }, 0, 0, layout.w, layout.h);
    ctx.globalCompositeOperation = 'destination-in';
    drawRaster(ctx, f.memory, ...crop);
    ctx.globalCompositeOperation = 'source-over';
  }
  record.image = image;
  return image;
}

const stateLabel = (c: Character, g: Game) => c.id === g.leaderId ? '조종 중' : c.state === 'captured' ? '갇힘' : c.state === 'carried' ? '잡힘' : c.state === 'hidden' ? '숨음' : c.state === 'groggy' ? '어질어질' : c.mode === 'wait' ? '대기' : '따라옴';
const friendColor = (c: Character, g: Game) => c.id === g.leaderId ? '#ffe6b8' : c.state === 'captured' || c.state === 'carried' ? '#df7770' : '#85b58b';

export function drawMap(g: Game, ui: CanvasRenderingContext2D): void {
  if (!sessions.has(g)) openMap(g);
  const session = sessions.get(g)!, f = g.mansion.get(g.mapFloor), record = session.floors.get(f.id)!;
  dimScreen(ui, 0.85);
  panel(ui, 32, 32, 896, 476, 'rgba(12,10,18,0.98)', '#a58b61');
  text(ui, '저택 지도', 56, 52, '#ffe6b8');
  text(ui, session.blueprint ? '저택 도면' : '탐색한 곳의 기록', 904, 52, '#a89a84', 'right');
  FLOORS.forEach((id, index) => {
    const x = 56 + index * 112, available = session.floors.has(id), active = g.mapFloor === id;
    panel(ui, x, 82, 104, 28, active ? '#39301e' : '#100e18', active ? '#e2b53c' : '#302938');
    text(ui, FLOOR_NAMES[id], x + 52, 88, active ? '#ffe6b8' : available ? '#aaa2b4' : '#494352', 'center', false);
  });
  const room = roomAtPx(g.floorOf(g.leader), g.leader.x, g.leader.y);
  text(ui, `현재 ${locationLabel(g.leader.floor, room?.name ?? '통로')}`, 904, 88, '#a89a84', 'right');
  ui.fillStyle = '#3a303b'; ui.fillRect(696, 128, 1, 318);
  text(ui, '이 층의 친구', 716, 130, '#d9d0b8');
  const friends = friendsOn(g, f.id);
  if (!friends.length) text(ui, '친구가 없다.', 716, 168, '#6f687c');
  friends.forEach((c, i) => {
    const y = 166 + i * 32, color = friendColor(c, g);
    ui.fillStyle = color; ui.fillRect(716, y + 4, 5, 5);
    text(ui, c.def.name, 732, y, color);
    text(ui, stateLabel(c, g), 904, y, '#938a9e', 'right');
  });
  let hovered = '';
  if (record.bounds) {
    const bounds = record.bounds, layout = mapLayout(bounds), scale = layout.scale;
    const ox = layout.x - bounds.x * scale, oy = layout.y - bounds.y * scale;
    drawRaster(ui, rememberedImage(f, record), layout.x, layout.y);
    ui.save(); ui.beginPath(); ui.rect(AREA.x, AREA.y, AREA.w, AREA.h); ui.clip();
    if (session.blueprint) {
      ui.strokeStyle = '#625849'; ui.lineWidth = 1;
      for (const r of f.rooms) ui.strokeRect(ox + r.x * scale + 0.5, oy + r.y * scale + 0.5, r.w * scale, r.h * scale);
    }
    for (const d of f.doors) {
      if (d.kind === 'secret' && !d.revealed || !session.blueprint && !isSeen(record, f, d.x, d.y)) continue;
      ui.fillStyle = d.locked ? '#c85e58' : '#81a6bd';
      ui.fillRect(ox + d.x * scale, oy + d.y * scale, Math.max(2, (d.orient === 'h' ? d.w : 1) * scale), Math.max(2, (d.orient === 'v' ? d.w : 1) * scale));
    }
    for (const s of f.stairs) {
      if (!session.blueprint && !isSeen(record, f, s.entry.x, s.entry.y)) continue;
      const x = ox + (s.entry.x + 0.5) * scale, y = oy + (s.entry.y + 0.5) * scale;
      ui.fillStyle = '#382b11'; ui.fillRect(x - 7, y - 8, 14, 18);
      text(ui, s.def.dir === 'up' ? '↑' : '↓', x, y - 7, '#e2b53c', 'center', false);
    }
    // Names live in the roster; clustered friends no longer overwrite each other.
    for (const c of [...friends.filter(c => c.id !== g.leaderId), ...friends.filter(c => c.id === g.leaderId)]) {
      const x = Math.round(ox + c.x / TILE * scale), y = Math.round(oy + c.y / TILE * scale), leader = c.id === g.leaderId;
      ui.fillStyle = '#0c0a12'; ui.fillRect(x - 4, y - 4, 8, 8);
      ui.fillStyle = friendColor(c, g); ui.fillRect(x - 2, y - 2, 4, 4);
      if (leader) { ui.strokeStyle = '#ffe6b8'; ui.lineWidth = 1; ui.strokeRect(x - 6.5, y - 6.5, 13, 13); }
    }
    const px = g.input.pointerX * 2, py = g.input.pointerY * 2;
    if (g.input.pointerKnown && px >= layout.x && px < layout.x + layout.w && py >= layout.y && py < layout.y + layout.h) {
      const tx = (px - ox) / scale, ty = (py - oy) / scale;
      if (session.blueprint || isSeen(record, f, tx, ty)) hovered = roomAtPx(f, tx * TILE, ty * TILE)?.name ?? '';
      ui.strokeStyle = '#e0d0b4'; ui.lineWidth = 1; ui.strokeRect(Math.round(px) - 3.5, Math.round(py) - 3.5, 7, 7);
    }
    ui.restore();
  }
  text(ui, hovered || (session.blueprint ? (g.input.touch ? '방을 누르면 이름이 보인다.' : '마우스를 올리면 방 이름이 보인다.') : '기억나는 길만 보인다. 도면을 찾으면 전체 배치가 보인다.'), 56, 460, '#8d829a');
  text(ui, '↑↓ 계단  파랑 문  빨강 잠금', 904, 460, '#9a8a70', 'right');
  text(ui, g.input.touch ? '층 이름을 눌러 바꾸기   닫기 버튼으로 나가기' : '←→ / 휠 층 보기   M / Esc 닫기   J 수첩', 480, 486, '#9b90a5', 'center');
}
